/**
 * User Firestore Service
 * Handles CRUD operations for users collection
 */
/* eslint-disable */
// @ts-nocheck
// @ts-ignore


import { database } from '../config/firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  serverTimestamp,
  increment,
  CollectionReference,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { User } from '@brovicms/models';
import { 
  UserFormData, 
  UserQueryOptions, 
  ServiceResult, 
  ListResult, 
  UserStats 
} from '../types/admin';

class UserService {
  private db: typeof database;
  private collectionName: string;
  private collection: CollectionReference<DocumentData>;

  constructor() {
    this.db = database;
    this.collectionName = 'users';
    this.collection = collection(this.db, this.collectionName);
  }

  /**
   * Validate user data
   * @param {Object} userData - User data to validate
   * @param {boolean} isUpdate - Whether this is an update operation
   * @returns {Object} Validation result
   */
  validateUserData(userData: Partial<UserFormData>, isUpdate = false): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required fields for new users
    if (!isUpdate) {
      if (!userData.email || userData.email.trim() === '') {
        errors.push('Email is required');
      }
    }

    // Email validation
    if (userData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userData.email)) {
        errors.push('Invalid email format');
      }
    }

    // Name validation
    if (userData.displayName && userData.displayName.length < 2) {
      errors.push('Display name must be at least 2 characters long');
    }

    if (userData.displayName && userData.displayName.length > 100) {
      errors.push('Display name must be less than 100 characters');
    }

    // Phone validation
    if (userData.phone) {
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      if (!phoneRegex.test(userData.phone.replace(/[\s\-\(\)]/g, ''))) {
        errors.push('Invalid phone number format');
      }
    }

    // Role validation
    if (userData.role && !['admin', 'moderator', 'user', 'premium'].includes(userData.role)) {
      errors.push('Invalid role. Must be admin, moderator, user, or premium');
    }

    // Subscription validation
    if (userData.subscriptionType && !['free', 'premium', 'enterprise'].includes(userData.subscriptionType)) {
      errors.push('Invalid subscription type. Must be free, premium, or enterprise');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Create a new user
   * @param {Object} userData - User data
   * @returns {Promise<Object>} Created user with ID
   */
  async createUser(userData: Partial<UserFormData>): Promise<ServiceResult<User>> {
    try {
      // Validate data
      const validation = this.validateUserData(userData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Check if user already exists
      const existingUser = await this.getUserByEmail(userData.email);
      if (existingUser.success) {
        throw new Error('User with this email already exists');
      }

      // Prepare document data
      const docData = {
        email: userData.email.toLowerCase().trim(),
        displayName: userData.displayName || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phone: userData.phone || '',
        avatar: userData.avatar || '',
        role: userData.role || 'user',
        subscriptionType: userData.subscriptionType || 'free',
        subscriptionStatus: userData.subscriptionStatus || 'active',
        subscriptionStartDate: userData.subscriptionStartDate || null,
        subscriptionEndDate: userData.subscriptionEndDate || null,
        preferences: {
          language: userData.preferences?.language || 'en',
          notifications: userData.preferences?.notifications || {
            email: true,
            push: true,
            sms: false
          },
          theme: userData.preferences?.theme || 'light',
          ...userData.preferences
        },
        profile: {
          bio: userData.profile?.bio || '',
          location: userData.profile?.location || '',
          website: userData.profile?.website || '',
          interests: userData.profile?.interests || [],
          ...userData.profile
        },
        stats: {
          totalReads: 0,
          totalDownloads: 0,
          totalPlayTime: 0,
          favoriteCategories: [],
          lastActiveAt: null
        },
        isActive: userData.isActive !== undefined ? userData.isActive : true,
        isVerified: userData.isVerified || false,
        isEmailVerified: userData.isEmailVerified || false,
        isPhoneVerified: userData.isPhoneVerified || false,
        lastLoginAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userData.createdBy || 'admin',
        lastModifiedBy: userData.createdBy || 'admin'
      };

      // Add document to Firestore
      const docRef = await addDoc(this.collection, docData);
      
      const result = {
        success: true,
        id: docRef.id,
        data: { ...docData, id: docRef.id }
      };

      toast.success('User created successfully');
      return result;

    } catch (error) {
      console.error('Create user error:', error);
      toast.error(`Failed to create user: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get user by ID
   * @param {string} id - User ID
   * @returns {Promise<Object>} User data
   */
  async getUser(id: string): Promise<ServiceResult<User>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('User not found');
      }

      return {
        success: true,
        id: docSnap.id,
        data: { ...docSnap.data(), id: docSnap.id }
      };
    } catch (error) {
      console.error('Get user error:', error);
      toast.error(`Failed to get user: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get user by email
   * @param {string} email - User email
   * @returns {Promise<Object>} User data
   */
  async getUserByEmail(email: string): Promise<ServiceResult<User>> {
    try {
      const q = query(
        this.collection,
        where('email', '==', email.toLowerCase().trim())
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return {
          success: false,
          message: 'User not found'
        };
      }

      const userDoc = querySnapshot.docs[0];
      return {
        success: true,
        id: userDoc.id,
        data: { ...userDoc.data(), id: userDoc.id }
      };
    } catch (error) {
      console.error('Get user by email error:', error);
      throw error;
    }
  }

  /**
   * Update user
   * @param {string} id - User ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Update result
   */
  async updateUser(id: string, updateData: Partial<UserFormData>): Promise<ServiceResult<User>> {
    try {
      // Validate data
      const validation = this.validateUserData(updateData, true);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const docRef = doc(this.db, this.collectionName, id);
      
      // Prepare update data
      const docData = {
        ...updateData,
        updatedAt: serverTimestamp(),
        lastModifiedBy: updateData.lastModifiedBy || 'admin'
      };

      // Clean up undefined values
      Object.keys(docData).forEach(key => {
        if (docData[key] === undefined) {
          delete docData[key];
        }
      });

      await updateDoc(docRef, docData);

      const result = {
        success: true,
        id,
        data: docData
      };

      toast.success('User updated successfully');
      return result;

    } catch (error) {
      console.error('Update user error:', error);
      toast.error(`Failed to update user: ${error.message}`);
      throw error;
    }
  }

  /**
   * Delete user
   * @param {string} id - User ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteUser(id: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      await deleteDoc(docRef);

      const result = {
        success: true,
        id,
        deletedAt: new Date().toISOString()
      };

      toast.success('User deleted successfully');
      return result;

    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(`Failed to delete user: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get all users with pagination and filtering
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Users list with pagination info
   */
  async getUsers(options: UserQueryOptions = {}): Promise<ListResult<User>> {
    try {
      const {
        limit: limitCount = undefined,
        orderBy: orderByField = 'createdAt',
        orderDirection = 'desc',
        role = null,
        subscriptionType = null,
        isActive = null,
        isVerified = null,
        search = null,
        startAfterDoc = null
      } = options;

      let q = query(this.collection);

      // Apply filters
      if (role) {
        q = query(q, where('role', '==', role));
      }

      if (subscriptionType) {
        q = query(q, where('subscriptionType', '==', subscriptionType));
      }

      if (isActive !== null) {
        q = query(q, where('isActive', '==', isActive));
      }

      if (isVerified !== null) {
        q = query(q, where('isVerified', '==', isVerified));
      }

      // Apply ordering
      q = query(q, orderBy(orderByField, orderDirection));

      // Apply pagination
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      }

      const querySnapshot = await getDocs(q);
      const users: User[] = [];
      let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;

      querySnapshot.forEach((doc) => {
        users.push({
          id: doc.id,
          ...doc.data()
        } as User);
        lastDoc = doc;
      });

      // Apply client-side search if provided
      let filteredUsers = users;
      if (search) {
        const searchLower = search.toLowerCase();
        filteredUsers = users.filter(user => 
          user.displayName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower)
        );
      }

      return {
        success: true,
        data: filteredUsers,
        pagination: {
          hasMore: users.length === limitCount,
          lastDoc,
          total: filteredUsers.length
        }
      };

    } catch (error) {
      console.error('Get users error:', error);
      toast.error(`Failed to get users: ${error.message}`);
      throw error;
    }
  }

  /**
   * Update user subscription
   * @param {string} id - User ID
   * @param {Object} subscriptionData - Subscription data
   * @returns {Promise<Object>} Update result
   */
  async updateSubscription(id: string, subscriptionData: any): Promise<ServiceResult<User>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      
      const updateData = {
        subscriptionType: subscriptionData.type,
        subscriptionStatus: subscriptionData.status || 'active',
        subscriptionStartDate: subscriptionData.startDate || null,
        subscriptionEndDate: subscriptionData.endDate || null,
        updatedAt: serverTimestamp(),
        lastModifiedBy: subscriptionData.modifiedBy || 'admin'
      };

      await updateDoc(docRef, updateData);

      const result = {
        success: true,
        id,
        data: updateData
      };

      toast.success('Subscription updated successfully');
      return result;

    } catch (error) {
      console.error('Update subscription error:', error);
      toast.error(`Failed to update subscription: ${error.message}`);
      throw error;
    }
  }

  /**
   * Update user role
   * @param {string} id - User ID
   * @param {string} role - New role
   * @returns {Promise<Object>} Update result
   */
  async updateRole(id: string, role: string): Promise<ServiceResult<User>> {
    try {
      if (!['admin', 'moderator', 'user', 'premium'].includes(role)) {
        throw new Error('Invalid role');
      }

      const docRef = doc(this.db, this.collectionName, id);
      await updateDoc(docRef, {
        role,
        updatedAt: serverTimestamp(),
        lastModifiedBy: 'admin'
      });

      const result = {
        success: true,
        id,
        role
      };

      toast.success('User role updated successfully');
      return result;

    } catch (error) {
      console.error('Update role error:', error);
      toast.error(`Failed to update role: ${error.message}`);
      throw error;
    }
  }

  /**
   * Update user verification status
   * @param {string} id - User ID
   * @param {Object} verificationData - Verification data
   * @returns {Promise<Object>} Update result
   */
  async updateVerification(id: string, verificationData: any): Promise<ServiceResult<User>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      
      const updateData = {
        isVerified: verificationData.isVerified || false,
        isEmailVerified: verificationData.isEmailVerified || false,
        isPhoneVerified: verificationData.isPhoneVerified || false,
        updatedAt: serverTimestamp(),
        lastModifiedBy: verificationData.modifiedBy || 'admin'
      };

      await updateDoc(docRef, updateData);

      const result = {
        success: true,
        id,
        data: updateData
      };

      toast.success('Verification status updated successfully');
      return result;

    } catch (error) {
      console.error('Update verification error:', error);
      toast.error(`Failed to update verification: ${error.message}`);
      throw error;
    }
  }

  /**
   * Update user activity
   * @param {string} id - User ID
   * @param {Object} activityData - Activity data
   * @returns {Promise<Object>} Update result
   */
  async updateActivity(id: string, activityData: any): Promise<ServiceResult<User>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      
      const updateData = {
        'stats.lastActiveAt': serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Update specific stats
      if (activityData.totalReads) {
        updateData['stats.totalReads'] = increment(activityData.totalReads);
      }
      if (activityData.totalDownloads) {
        updateData['stats.totalDownloads'] = increment(activityData.totalDownloads);
      }
      if (activityData.totalPlayTime) {
        updateData['stats.totalPlayTime'] = increment(activityData.totalPlayTime);
      }

      await updateDoc(docRef, updateData);

      return {
        success: true,
        id
      };
    } catch (error) {
      console.error('Update activity error:', error);
      throw error;
    }
  }

  /**
   * Get user statistics
   * @returns {Promise<Object>} Statistics data
   */
  async getUserStats() {
    try {
      const q = query(this.collection);
      const querySnapshot = await getDocs(q);
      
      let totalUsers = 0;
      let activeUsers = 0;
      let verifiedUsers = 0;
      let premiumUsers = 0;
      let adminUsers = 0;
      const roles = {};
      const subscriptionTypes = {};
      const recentSignups = 0;

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        totalUsers++;
        
        if (data.isActive) activeUsers++;
        if (data.isVerified) verifiedUsers++;
        if (data.subscriptionType === 'premium') premiumUsers++;
        if (data.role === 'admin') adminUsers++;
        
        const role = data.role || 'user';
        roles[role] = (roles[role] || 0) + 1;
        
        const subscriptionType = data.subscriptionType || 'free';
        subscriptionTypes[subscriptionType] = (subscriptionTypes[subscriptionType] || 0) + 1;
        
        // Check recent signups (last 7 days) — handle Timestamp or Date/string/number
        if (data.createdAt) {
          let createdAtDate: Date | null = null;
          try {
            if ((data.createdAt as any)?.toDate && typeof (data.createdAt as any).toDate === 'function') {
              createdAtDate = (data.createdAt as any).toDate();
            } else if (data.createdAt instanceof Date) {
              createdAtDate = data.createdAt as Date;
            } else if (typeof data.createdAt === 'number') {
              createdAtDate = new Date(data.createdAt);
            } else {
              createdAtDate = new Date(data.createdAt as any);
            }
          } catch (e) {
            createdAtDate = null;
          }

          if (createdAtDate && createdAtDate > oneWeekAgo) {
            recentSignups++;
          }
        }
      });

      return {
        success: true,
        stats: {
          totalUsers,
          activeUsers,
          verifiedUsers,
          premiumUsers,
          adminUsers,
          recentSignups,
          roles,
          subscriptionTypes
        }
      };
    } catch (error) {
      console.error('Get user stats error:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const userService = new UserService();
export default userService;
